<template>
  <section id="product-detail-section-faq" class="faq card">
    <h3 class="title">{{ $t('category_faq_title') }}</h3>
    <nd-collapse :list="faqCollapseData" :divider="true" class="!px-0" content-class="whitespace-pre-wrap"></nd-collapse>
  </section>
</template>

<script setup lang="ts">
import { parseISO } from 'date-fns'
import type { LangEnum } from '@/i18n.config'
import { HAN_RELATED_LANGS } from '@/i18n.config'

import { DayModeCountMap } from '@/types/filters'
import type { UiProductInfo } from '@/types/product'
import { EdbCategoryDayMode as ApiEdbCategoryDayMode } from '@/types/api'

const { format } = await useI18nDateFns()

const { locale, t } = useI18n()

const props = defineProps<{
  product: UiProductInfo
}>()

const productName = computed(() => props.product?.product_name)

const dayMode = computed(() => props.product?.time_mode || ApiEdbCategoryDayMode.CategoryDayModeDefault)
const dayModeCount = computed(() => (dayMode.value ? DayModeCountMap[dayMode.value] : DayModeCountMap[ApiEdbCategoryDayMode.CategoryDayModeDefault]))
const prepareTime = computed(() => Math.round(props.product?.prepare_time ? props.product?.prepare_time / 60 / 24 : 0))

const recentlySellTime = computed(() => props.product?.sell_time || null)

const transportation = computed(() => props.product?.transportation || null)

const separator = computed(() => (HAN_RELATED_LANGS.includes(locale.value as LangEnum) ? '、' : ','))
const facilitiesString = computed(() => props.product?.facilities?.map(facility => facility.name).join(separator.value) || null)

const faqCollapseData = computed(() => {
  if (!props.product) return []

  return [
    {
      title: t('product_faq_question_1', { product_name: productName.value }),
      content: t('product_faq_ans_1', { product_name: productName.value, time_mode: dayModeCount.value, days: prepareTime.value }),
    },
    ...(recentlySellTime.value
      ? [
          {
            title: t('product_faq_question_2', { product_name: productName.value }),
            content: `${format(parseISO(recentlySellTime.value), 'yyyy/MM/dd HH:mm')}<br/>${t('product_faq_ans_2')}`,
          },
        ]
      : []),
    ...(transportation.value ? [{ title: t('product_faq_question_3', { product_name: productName.value }), content: transportation.value }] : []),
    ...(facilitiesString.value
      ? [
          {
            title: t('product_faq_question_4', { product_name: productName.value }),
            content: t('product_faq_ans_4', { product_name: productName.value, facilities: facilitiesString.value }),
          },
        ]
      : []),
  ]
})
</script>

<style lang="scss" scoped></style>
